import { Component } from 'react'

import { Checkbox } from '@joor/design-system'
import classNames from 'classnames'
import debounce from 'lodash/debounce'
import isEmpty from 'lodash/isEmpty'
import PropTypes from 'prop-types'

import { formatRetailerWithLocation } from 'formatters/orders'
import { formatDropdownOptions } from 'utils/formatters'

import { Dropdown, Icon, Input, Loader, Modal } from 'components/Core'
import AccountDropdown, {
  ACCOUNT_ID,
  PROFILE_NAME,
  RETAILER,
} from 'components/Core/AccountDropdown'

import { messages } from './AssignRetailerAccountModal.messages'
import styles from './AssignRetailerAccountModal.module.css'
import { MUTATION_NAME } from './AssignRetailerAccountModal.mutations'
import { MINIMUM_CHARACTERS_QUERY } from './AssignRetailerAccountModal.queries'

export const DEBOUNCE_TIME = 500

export default class AssignRetailerAccountModal extends Component {
  state = {
    contactEmail: '',
    retailerOptions: [],
    retailer: {
      id: null,
      isConnected: false,
      isPro: false,
      name: '',
    },
    keepAddressInfo: false,
  }

  componentDidUpdate(prevProps) {
    const finishedLoading =
      prevProps.publicAccountsQueryLoading &&
      !this.props.publicAccountsQueryLoading
    const searchTextChanged =
      prevProps.retailerSearchText !== this.props.retailerSearchText
    const minimumCharactersReached =
      this.props.retailerSearchText.length < MINIMUM_CHARACTERS_QUERY

    if (finishedLoading || searchTextChanged) {
      this.setRetailerOptions(minimumCharactersReached)
    }
  }

  componentWillUnmount() {
    this.props.changeRetailerSearchText('')
    this.setContactEmailAndRemoveItFromQuery('')
  }

  onRetailerDropdownChange = (_, { value }) => {
    if (value !== this.props.selectedRetailerId) {
      this.props.setSelectedRetailerId(value)
    }
  }

  onAssign = () => {
    const {
      handleUpdateOrderRetailerAccount,
      selectedOrdersIds,
      selectedRetailerId,
      setPageLoaderActive,
      handleResetOrder,
      renderActionsToasterMessage,
      trackAssignClick,
    } = this.props
    const { contactEmail, retailer, keepAddressInfo } = this.state

    const isConnected = retailer.id === null ? true : retailer.isConnected
    trackAssignClick(isConnected)
    setPageLoaderActive(true)
    handleUpdateOrderRetailerAccount({
      selectedOrdersIds,
      contactEmail,
      selectedRetailerId,
      keepAddressInfo,
    })
      .then(({ data }) => {
        const returnedOrders = data[MUTATION_NAME]

        handleResetOrder()
        setPageLoaderActive(false)

        renderActionsToasterMessage(
          returnedOrders.updatedOrders.length,
          returnedOrders.notUpdatedOrders.length,
        )
      })
      .catch((error) => {
        console.error(error)
        setPageLoaderActive(false)
      })
  }

  setRetailerOptions = (minimumCharactersReached) => {
    this.setState({
      retailerOptions: minimumCharactersReached
        ? []
        : this.getRetailerOptions(),
    })
  }

  getRetailerOptions = () => {
    const { canAssignOrdersToNewConnections } = this.props

    if (canAssignOrdersToNewConnections) {
      return this.formatRetailerOptions()
    }
    return formatDropdownOptions(
      this.props.retailers,
      formatRetailerWithLocation,
      true,
      'notranslate',
    )
  }

  getRetailerDropdownMessage = () => {
    const {
      retailer: { id, isPro, name },
    } = this.state

    if (!id) {
      return messages.retailerNotFound(name)
    }
    if (isPro) {
      return messages.sendConnectionRequest(name)
    }
    return messages.enterRetailerContactEmail(name)
  }

  setContactEmailAndRemoveItFromQuery = (contactEmail) => {
    const {
      changeRetailerContactEmail,
      changeRetailerId,
      retailerContactEmail,
    } = this.props
    this.setState({ contactEmail })
    if (retailerContactEmail) {
      changeRetailerContactEmail('')
      changeRetailerId(null)
    }
  }

  handleKeepAddressInfoCheckbox = () => {
    this.setState({ keepAddressInfo: !this.state.keepAddressInfo })
  }

  handleAccountChange = (account) => {
    const { selectedRetailerId, setSelectedRetailerId } = this.props

    this.setState({ retailer: { ...account } })
    if (account[ACCOUNT_ID] !== selectedRetailerId) {
      setSelectedRetailerId(account[ACCOUNT_ID])
    }
    if (!account[ACCOUNT_ID]) {
      this.updateRetailersQuery(account[PROFILE_NAME])
    }
    if (this.state.contactEmail) {
      this.setContactEmailAndRemoveItFromQuery('')
    }
  }

  formatRetailerOptions = () =>
    this.props.retailers.map((retailer) => ({
      id: retailer.id,
      name: retailer.name,
      isPro: retailer.isPro,
      isConnected: retailer.isConnected,
      location: !isEmpty(retailer.locations) ? retailer.locations[0] : {},
    }))

  handleSearchRetailer = (e, { searchQuery }) =>
    this.updateRetailersQuery(searchQuery)

  updateRetailersQuery = debounce((searchText) => {
    if (searchText !== this.props.retailerSearchText) {
      this.props.changeRetailerSearchText(searchText)
    }
  }, DEBOUNCE_TIME)

  handleSearchContact = () => {
    const { changeRetailerContactEmail, changeRetailerId } = this.props
    const {
      contactEmail,
      retailer: { id: retailerId },
    } = this.state
    changeRetailerContactEmail(contactEmail)
    changeRetailerId(retailerId)
  }

  handleContactEmailChange = (e, { value }) => {
    this.setContactEmailAndRemoveItFromQuery(value)
  }

  handleContactEmailKeyPressed = (e) => {
    if (e.key === 'Enter') {
      this.handleSearchContact()
    }
  }

  shouldDisableAction = () => {
    const {
      retailer: { isConnected, isPro },
    } = this.state
    const {
      canAssignOrdersToNewConnections,
      retailerContactEmail,
      selectedRetailerId,
    } = this.props

    if (canAssignOrdersToNewConnections) {
      return (
        !selectedRetailerId || (!isConnected && !isPro && !retailerContactEmail)
      )
    }
    return !selectedRetailerId
  }

  renderContactEmailMessage = () => {
    const {
      contactEmail,
      retailerOptions,
      retailer: { name },
    } = this.state
    const getMessage = retailerOptions.length
      ? messages.retailerContactFound
      : messages.retailerContactNotFound
    return (
      <div className={classNames(styles.textContainer, styles.green)}>
        {getMessage(contactEmail, name)}
      </div>
    )
  }

  renderContactDropdown = () => {
    const { publicAccountsQueryLoading, retailerContactEmail } = this.props
    const shouldRenderContactEmailMessage =
      retailerContactEmail && !publicAccountsQueryLoading

    return (
      <>
        <div className={styles.contactContainer}>
          <label className={styles.nameLabel} htmlFor="contactEmail">
            {messages.retailerEmail}
          </label>
          <Input
            onChange={this.handleContactEmailChange}
            onKeyPress={this.handleContactEmailKeyPressed}
            icon={
              <Icon
                className={styles.searchIcon}
                name="searchSmall"
                onClick={this.handleSearchContact}
              />
            }
            name="contactEmail"
            placeholder={messages.searchByEmail}
            value={this.state.contactEmail}
          />
          {shouldRenderContactEmailMessage && this.renderContactEmailMessage()}
        </div>
      </>
    )
  }

  renderAccountDropdown = () => {
    const {
      retailerOptions,
      retailer: { isConnected, isPro, name },
    } = this.state
    const {
      canAssignOrdersToNewConnections,
      publicAccountsQueryLoading,
      selectedRetailerId,
      retailerSearchText,
    } = this.props

    const showContainer =
      !publicAccountsQueryLoading &&
      retailerSearchText.length >= MINIMUM_CHARACTERS_QUERY &&
      !isConnected
    if (canAssignOrdersToNewConnections) {
      return (
        <>
          <AccountDropdown
            accountType={RETAILER}
            accounts={retailerOptions}
            handleAccountChange={this.handleAccountChange}
            placeholder={messages.startTyping}
            profileName={name}
            required
          />
          {showContainer && (
            <div
              className={classNames(styles.textContainer, {
                [styles.green]: isPro,
                [styles.yellow]: !isPro,
              })}
            >
              {this.getRetailerDropdownMessage()}
            </div>
          )}
        </>
      )
    }
    return (
      <Dropdown
        onChange={this.onRetailerDropdownChange}
        onSearchChange={this.handleSearchRetailer}
        options={retailerOptions}
        placeholder={messages.startTyping}
        search
        value={selectedRetailerId}
        className={selectedRetailerId ? 'notranslate' : ''}
      />
    )
  }

  renderKeepAddressInfoCheckbox = () => {
    const { keepAddressInfo } = this.state

    return (
      <Checkbox
        id="keepAddressInfoCheckbox"
        checked={keepAddressInfo}
        label={messages.keepAddressInfo}
        onChange={this.handleKeepAddressInfoCheckbox}
        size="regular"
        className={styles.checkbox}
      />
    )
  }

  render() {
    const {
      retailer: { id, isConnected, isPro },
    } = this.state
    const {
      onClose,
      publicAccountsQueryLoading,
      canAssignOrdersToNewConnections,
    } = this.props
    const shouldRenderContactDropdown =
      canAssignOrdersToNewConnections && id && !isConnected && !isPro
    const shouldDisplaySendRequestLabel =
      canAssignOrdersToNewConnections && id && !isConnected && isPro
    const primaryActionLabel = shouldDisplaySendRequestLabel
      ? messages.sendRequest
      : messages.assign

    return (
      <Modal
        open
        onClose={onClose}
        primaryActionLabel={primaryActionLabel}
        title={messages.assignRetailerAccount}
        primaryDisabled={this.shouldDisableAction()}
        primaryActionOnClick={this.onAssign}
        className={styles.AssignRetailerAccount}
      >
        <Loader useFullScreen active={publicAccountsQueryLoading} />
        <p className={styles.selectLabel}>{messages.selectRetailer}</p>
        {this.renderAccountDropdown()}
        {this.renderKeepAddressInfoCheckbox()}
        {shouldRenderContactDropdown && this.renderContactDropdown()}
        {!canAssignOrdersToNewConnections && (
          <p className={styles.noteLabel}>{messages.note}</p>
        )}
        <p className={styles.retailerDiscountInfo}>
          {messages.retailerDiscountInfo}
        </p>
      </Modal>
    )
  }
}

AssignRetailerAccountModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  retailers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      displayName: PropTypes.string,
      locations: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          city: PropTypes.string,
          state: PropTypes.string,
        }),
      ),
    }),
  ),
  changeRetailerContactEmail: PropTypes.func.isRequired,
  changeRetailerId: PropTypes.func.isRequired,
  changeRetailerSearchText: PropTypes.func.isRequired,
  retailerContactEmail: PropTypes.string.isRequired,
  retailerSearchText: PropTypes.string.isRequired,
  selectedRetailerId: PropTypes.string,
  setSelectedRetailerId: PropTypes.func.isRequired,
  handleResetOrder: PropTypes.func.isRequired,
  publicAccountsQueryLoading: PropTypes.bool,
  selectedOrdersIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  handleUpdateOrderRetailerAccount: PropTypes.func.isRequired,
  setPageLoaderActive: PropTypes.func.isRequired,
  renderActionsToasterMessage: PropTypes.func.isRequired,
  trackAssignClick: PropTypes.func,
  canAssignOrdersToNewConnections: PropTypes.bool,
}

AssignRetailerAccountModal.defaultProps = {
  publicAccountsQueryLoading: false,
  retailers: [],
  selectedRetailerId: '',
  trackAssignClick: () => {},
  canAssignOrdersToNewConnections: PropTypes.bool,
}
